import {
  DirentNameEnum,
  GroupFilePermissions,
  GroupFilePermissionsMap,
  OrganizationPolicy,
} from '@orgbrain/lib-data';
import { SigneeV2, SignOrderSummary } from '@orgbrain/lib-signing';
import { permissionDescription, PermissionEnum } from '@orgbrain/roles';
import { Session } from './lib-admin';
import { ACLBrief, FileInfo } from './lib-files';
import { RuleResult } from './org-auth/org-auth-classes';

export class DirentUsageMap {
  map: DirentUsage;
}

export class DirentUsageItem {
  parentId: string;
  // size: number;
  fileSize: number;
  // Meeetings have name
  name?: string;
  fileReviewMetaSideOne?: any;
  fileReviewMetaSideTwo?: any;
  isFolder?: boolean;
  children?: DirentUsageItem[];
  _id?: string;
  acl?: ACLMap;
  ecl?: ACLMap;
  scl?: ACLMap;
  direntFileChildrenCount?: number;
  deleted?: boolean;
}
export class DirentUsage {
  [id: string]: DirentUsageItem;
}

export function aclToBrief(acl: Partial<ACLBrief>, stripOrg: boolean = true): ACLBrief {
  if (!acl) {
    return undefined;
  }
  const brief: ACLBrief = {} as ACLBrief;
  for (let group of Object.keys(acl || {})) {
    const aclGroup: ACLMap = acl[group];

    if (stripOrg) {
      group = group.substring(25);
    }
    if (aclGroup.WRITE && aclGroup.READ) {
      brief[group] = 'RW';
    } else if (aclGroup.READ) {
      brief[group] = 'R';
    } else if (aclGroup.WRITE) {
      brief[group] = 'W';
    }
    return brief;
  }
}
export enum PrivilegeType {
  WRITE = 'WRITE',
  READ = 'READ',
}

export enum PrivilegeTypeEnum {
  READ = 'READ',
  READWRITE = 'READWRITE',
  NOACCESS = 'NOACCESS',
  MIXED = 'MIXED',
  WRITEDENY = 'WRITEDENY',
}

export enum BasicPrivilegeTypeEnum {
  READ = PrivilegeTypeEnum.READ,
  READWRITE = PrivilegeTypeEnum.READWRITE,
}

export type DirentVisitor = (dirent: DirentNode) => Promise<void>;

export const privilegeEunumToFriendly = {
  READ: { en: 'Read only', nb: 'Skrivebeskyttet' },
  READWRITE: { en: 'Read & write', nb: 'Lese- og skriverettigheter' },
  NOACCESS: { en: 'No access', nb: 'Ingen rush s' },
  WRITEDENY: { en: 'Deny write', nb: 'Nekt skriv' },
  MIXED: { en: 'Mixed', nb: 'Blandet' },
};

export type DirentPermissionMap = { [group: string]: BasicPrivilegeTypeEnum };

export class DirentUser {
  userId: string; //
  groups: string[];
  super: boolean;
}

export interface ACLPrivileges {
  READ?: boolean;
  WRITE?: boolean;
}

export function aclPrivilegeDisplay(privs, lang) {
  if (privs['READ'] && privs['WRITE']) return privilegeEunumToFriendly.READWRITE[lang];
  if (privs['READ']) return privilegeEunumToFriendly.READ[lang];
  return '????';
}

export function eclPrivilegeDisplay(privs, lang) {
  if (privs['READ'] && privs['WRITE']) return privilegeEunumToFriendly.NOACCESS[lang];
  if (privs['WRITE']) return privilegeEunumToFriendly.WRITEDENY[lang];
  return '????';
}

export function sclPrivilegeDisplay(privs, lang) {
  if (privs['READ'] && privs['WRITE']) return privilegeEunumToFriendly.READWRITE[lang];
  if (privs['READ']) return privilegeEunumToFriendly.READ[lang];
  return '????';
}

// conveniance constants

export const ALLOW_READ_WRITE: ACLPrivileges = { READ: true, WRITE: true };
export const FORBID_READ_WRITE: ACLPrivileges = { READ: true, WRITE: true };

export const ALLOW_READ: ACLPrivileges = { READ: true };
export const FORBID_WRITE: ACLPrivileges = { WRITE: true };

export interface ACLMap {
  [group: string]: ACLPrivileges;
}
export const NULL = {};

export enum ACLPrivilegeConstantKey {
  ALLOW_READ_WRITE = 'ALLOW_READ_WRITE',
  ALLOW_READ = 'ALLOW_READ',
  NULL = 'NULL',
}

export const ACLPrivilegeConstant: { [key in ACLPrivilegeConstantKey]: ACLPrivileges } = {
  ALLOW_READ_WRITE,
  ALLOW_READ,
  NULL,
};

export class SharedFolderAssociationRule {
  organization: any;
  hasWritePermission: boolean;
  modifiedBy: string;
}

export class FileStatusMeta {
  status: FileReviewStatus;
  responsible: string;
}
export class DirentNode {
  _id?: any;
  organizationId: string;
  name: string;
  parentId: string;
  direntReferenceId?: string;
  direntReferenceOrganizationId?: string;
  // This is all the ancestors of the dirent. (mongodbIds)
  pathIds?: string[];
  isFolder: boolean;
  acl?: ACLMap; //  if missing inherit from parent;
  ecl?: ACLMap; // exlusion filter
  scl?: ACLMap; // shared with
  createdBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
  deleted?: boolean;
  nameType: DirentNameEnum;
  fileSize: number;
  direntPermissionsMap?: DirentPermissionMap;
  hash?: string;
  debugFileSize?: string;
  // local fields   see context before using any of this.
  direntPath?: string[];
  groupName?: string; // temp for display
  file?: FileInfo; // temp for signinginfo in front end browser
  displayName?: string; // temp to store translated name.
  displayEmail?: string; //temp
  fullPath?: string;

  mark?: boolean; // temporay for admion-db-ops
  signRequestSummary?: string; // Frontend marker if has a sign request.
  historyRecords?: FileLogType[];
  signOrder?: SignOrderSummary;
  annotationsCount?: number;
  versionCnt?: number;
  viewed?: Date;
  signSummary?: string;
  selected?: boolean;
  signedFileKey?: string;
  hasSignRequest?: boolean; // Frontend marker if has a sign request.
  placeholder?: string;
  sharedAssociationRules?: SharedFolderAssociationRule[];
  sharedWithOrganizations?: string[];
  dirty?: boolean;

  fileReviewMetaSideOne?: FileStatusMeta;
  fileReviewMetaSideTwo?: FileStatusMeta;

  modifiedBy?: string;
  contentType?: string;
  oldName?: string;

  // UI only
  ok?: boolean; // used in file tree to mark if ok

  // Hack to get the dispaly name of shared file.
  sharedRootName?: string; // used in file tree to mark if ok

  systemFolder?: boolean; // System folders are not displayed in the Dataroom UI
}
export class TreeNode {
  dirent: DirentNode;
  children: { [name: string]: TreeNode };
}

export interface DirentFolder {
  node: DirentNode;
  children: DirentNode[];
}
export enum DirentErrorType {
  PRIVILEGE_ERROR = 'PRIVILEGE_ERROR',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  EXISITING_UNEXPECTED_TYPE = 'EXISITING_UNEXPECTED_TYPE',
}

export class PrivilegeError extends Error {
  constructor(message?: string) {
    super(message);
  }
  type = DirentErrorType.PRIVILEGE_ERROR;
}

export class AlreadyExistsError extends Error {
  type = DirentErrorType.ALREADY_EXISTS;
}

export class ExistingDirentUnexpectedTypeError extends Error {
  type = DirentErrorType.EXISITING_UNEXPECTED_TYPE;
}

export enum FileReviewStatus {
  FOLLOW_UP = 'FOLLOW_UP',
  INCOMPLETE = 'INCOMPLETE',
  COMPLETE = 'COMPLETE',
}

export class ControlLists {
  acl?: ACLMap;
  ecl?: ACLMap;
  scl?: ACLMap;
}

export class DirentFolderInfo {
  node: DirentNode;
  children: DirentNode[];
  controlLists: ControlLists;
  pathdata?: DirentNode[];
  realDirentAfterFake?: DirentNode;
  isSharedFake: boolean;
}

// If you edit the below ENUM
// make sure to add translations...
// Search for FileLogAction
// Or STATUSFileLogAction as example
export enum FileLogCatergory {
  DOWNLOAD = 'DOWNLOAD',
  VIEW = 'VIEW',
  COPY = 'COPY',
  RENAME = 'RENAME',
  DELETED = 'DELETED',
  RESTORED = 'RESTORED',
  CREATED = 'CREATED',
  PERMISSIONS = 'PERMISSIONS',
  VIEW_SIGNED = 'VIEW_SIGNED',
  DOWNLOAD_WATERMARKED = 'DOWNLOAD_WATERMARKED',
  STATUS = 'STATUS',
  RESPONSIBLE = 'RESPONSIBLE',
  // READ THE COMMENT ABOVE THE ENUM!!!
}

// export class FileLogRecord {
//   userId: string;
//   direntId: string;
//   organizationId: string;
//   type: FileLogCatergory;
//   stamp: string;
//   data: string;
// }

// fileIdList  all files for this operation.
// folderIdList  all the folders that are under the path
// pathIdList  path to the folder  that is the root of the operation.

export interface FileLogType {
  userId: string;
  fileIdList: string[];
  folderIdList: string[];
  pathIdList: string[];
  organizationId: string;
  type: FileLogCatergory;
  stamp?: Date;
  data?: any | string;
  // Front end on the fly
  userNameDisplay?: any;
  direntId?: string;
  display?: string;
}

export class FileUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export type FileHistoryPathMap = { [id: string]: { name: string; parentId: string } };
export class FileHistoryMessage {
  items: FileLogType[];
  users: FileUser[];
  paths: FileHistoryPathMap;
}

export const defaultGroupFilePermissionsMap: GroupFilePermissionsMap = {};

defaultGroupFilePermissionsMap[
  permissionDescription[PermissionEnum.ACCESS_TO_BOARD_MEETINGS].group
] = { download: true, watermark: false };

defaultGroupFilePermissionsMap[permissionDescription[PermissionEnum.ACCESS_TO_GA_MEETINGS].group] =
  { download: true, watermark: false };

defaultGroupFilePermissionsMap[permissionDescription[PermissionEnum.ADMIN].group] = {
  download: true,
  watermark: false,
};

defaultGroupFilePermissionsMap[permissionDescription[PermissionEnum.BUYER].group] = {
  download: true,
  watermark: true,
};

export type InviterSummary = { firstName: string; lastName: string; email: string; _id: string };
export class FileDownloadResponce {
  file;
  name: string;
  signee: SigneeV2;
  history: FileLogType[];
  filePerm: GroupFilePermissions;
  dirent: DirentNode;
  message: string;
  inviter: InviterSummary;
  mimetype: string;
  signicatArchive: boolean;
  hasSignatures: boolean;
  fullPath: string;
  canWrite: boolean;
  hasPermission: boolean;
  contentType: string;
}

// Return value from the security services to see state of security (e.g. about to expire)
export type PreemptiveSecurityReport = {
  // assertLoginCredentials: CredentialEnum[];      // MOVED TO PARAMS
  nowOverride: number;
  result: RuleResult;
  testPolicy: OrganizationPolicy; // Set this to override the organization  policy.
  session: Session; //  If this is set it should override the session in user.  It shoudl be the same but we will have the inactivity times updated to reflect the current time.
  reason: string;
  // willExpireAfterMillis: number;
};
